import {
    ALL_INSURANCE_PROVIDERS,
    IClient,
    InsuranceType,
    RelationshipToPrimaryInsured,
    STATES,
} from "intake_shared/src/types";
import { TUpdateClient } from "../../clientUiTypes";
import { toTitleCase } from "../../../../../utils/utils";
import { Select } from "../../../bits/Select/Select";
import { Input } from "../../../bits/Input/Input";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";

type Option = {
    label: React.ReactNode;
    value: string;
};

function NewClientInsuranceForm({
    client,
    updateClient,
    viewingInsuranceVerification,
    viewingAppointmentSetting = false,
}: {
    client: IClient;
    updateClient: TUpdateClient;
    viewingInsuranceVerification?: boolean;
    viewingAppointmentSetting?: boolean;
}) {
    const getClientStateFieldSelectOptions = () => {
        return STATES.map((state) => {
            return {
                label: state.name,
                value: state.abbrv,
            };
        });
    };

    const getInsuranceProviderOptions = ALL_INSURANCE_PROVIDERS.map((p) => ({
        label: p.toUpperCase(),
        value: p.toUpperCase(),
    }));

    const getInsuranceTypeOptions = Object.values(InsuranceType).map((p) => ({
        label: toTitleCase(p.replaceAll("_", " ")),
        value: p,
    }));

    const getRelationshipOptions = Object.values(RelationshipToPrimaryInsured).map(
        (p) => ({
            label: toTitleCase(p),
            value: p,
        })
    );
    const getAcceptAssignmentOptions = [
        {
            label: "Yes",
            value: "Y",
        },
        {
            label: "No",
            value: "N",
        },
    ];

    return (
        <div className="v2-form">
            {!viewingAppointmentSetting && (
                <div className="gridThree">
                    <Select
                        label="Insurance provider"
                        isSearchable
                        onChange={(val) =>
                            updateClient(
                                "insurance_provider",
                                (val as Option).value.toUpperCase()
                            )
                        }
                        options={[
                            { label: "Select option", value: "" },
                            ...getInsuranceProviderOptions,
                        ]}
                        value={getInsuranceProviderOptions.find(
                            (item) =>
                                item.value ===
                                client.insurance_provider?.toUpperCase()
                        )}
                    />
                    <Input
                        label="Insurance member ID"
                        value={client.insurance_member_id || ""}
                        onChange={(e) => {
                            updateClient("insurance_member_id", e.target.value);
                        }}
                    />
                    <Select
                        label="Insurance type"
                        onChange={(val) =>
                            updateClient("insurance_type", (val as Option).value)
                        }
                        options={[
                            { label: "Select option", value: "" },
                            ...getInsuranceTypeOptions,
                        ]}
                        value={getInsuranceTypeOptions.find(
                            (item) => item.value === client.insurance_type
                        )}
                    />
                </div>
            )}
            {viewingInsuranceVerification && (
                <div className="gridThree">
                    <Input
                        label="Address line"
                        value={client.address}
                        onChange={(e) => {
                            updateClient("address", e.target.value);
                        }}
                    />
                    <Select
                        id="state"
                        label="State"
                        isSearchable
                        onChange={(val) =>
                            updateClient("state", (val as Option).value)
                        }
                        options={[
                            { label: "Select option", value: "" },
                            ...getClientStateFieldSelectOptions(),
                        ]}
                        value={getClientStateFieldSelectOptions().find(
                            (state) => state.value === client.state
                        )}
                    />
                    <Input
                        label="City"
                        value={client.city}
                        onChange={(e) => {
                            updateClient("city", e.target.value);
                        }}
                    />
                </div>
            )}
            <div className="gridThree">
                <Select
                    label="Relationship to Primary Insured"
                    onChange={(val) =>
                        updateClient(
                            "relationship_to_primary_insured",
                            (val as Option).value
                        )
                    }
                    options={[
                        { label: "Select option", value: "" },
                        ...getRelationshipOptions,
                    ]}
                    value={getRelationshipOptions.find(
                        (item) =>
                            item.value === client.relationship_to_primary_insured
                    )}
                />
                <Input
                    label="Insurance plan employer"
                    value={client.insurance_plan_employer}
                    onChange={(e) => {
                        updateClient("insurance_plan_employer", e.target.value);
                    }}
                />
                <Input
                    label="Group Number"
                    value={client.group_number}
                    onChange={(e) => {
                        updateClient("group_number", e.target.value);
                    }}
                />
                {viewingInsuranceVerification ? (
                    <Input
                        label="Zip code"
                        value={client.zip_code}
                        onChange={(e) => {
                            updateClient("zip_code", e.target.value);
                        }}
                    />
                ) : (
                    <Select
                        options={[
                            { label: "Select option", value: "" },
                            ...getAcceptAssignmentOptions,
                        ]}
                        label="Accepts assignment"
                        onChange={(val) =>
                            updateClient("accept_assignment", (val as Option).value)
                        }
                        defaultValue={getAcceptAssignmentOptions.find(
                            (item) => item.value === client.accept_assignment
                        )}
                    />
                )}
            </div>
            <div className="gridThree">
                {viewingInsuranceVerification && (
                    <Select
                        options={[
                            { label: "Select option", value: "" },
                            ...getAcceptAssignmentOptions,
                        ]}
                        label="Accepts assignment"
                        onChange={(val) =>
                            updateClient("accept_assignment", (val as Option).value)
                        }
                        defaultValue={getAcceptAssignmentOptions.find(
                            (item) => item.value === client.accept_assignment
                        )}
                    />
                )}

                <FormCheckbox
                    label="Client is B2B"
                    checked={client.client_is_b2b}
                    onChange={() =>
                        updateClient("client_is_b2b", !client.client_is_b2b)
                    }
                    inForm
                />
            </div>
        </div>
    );
}

export default NewClientInsuranceForm;
